<script setup lang="ts">
import { onMounted, reactive, ref, watch } from 'vue'
import type { RepayParentRow, VoucherRow } from '@/api/modules/repay'
import { fetchRepayDetails, fetchRepayParents } from '@/api/modules/repay'
import { formatEpoch, formatMoneyInt, formatServerDateText } from '@/utils/format'

/** 对标 Admin/Daikuan/voucher · 待还；并联「已还订单」pending=false */
type RepayTab = 'awaiting' | 'repaid'

const tab = ref<RepayTab>('awaiting')
const q = reactive({ keyword: '', page: 1, rows: 10 })
const loading = ref(false)
const rows = ref<RepayParentRow[]>([])
const total = ref(0)

const detailDlg = ref(false)
const detailLoading = ref(false)
const detailTitle = ref('')
const detailRows = ref<VoucherRow[]>([])
const detailOrderNum = ref('')

function pendingQuery(): boolean {
  return tab.value === 'awaiting'
}

function fmtMoney(n?: number | null) {
  return formatMoneyInt(n)
}

function instNo(r: VoucherRow) {
  return r.currentInstallment ?? r.ofnumber ?? undefined
}

function fmtDt(s?: string | null) {
  return formatServerDateText(s, '—')
}

function voucherStatusLabel(st?: number | null) {
  if (st == null) return '—'
  return Number(st) === 1 ? '已还' : '待还'
}

async function load() {
  loading.value = true
  try {
    const { data } = await fetchRepayParents(
      pendingQuery(),
      q.keyword || undefined,
      Math.max(1, q.page),
      q.rows,
    )
    rows.value = data.content ?? []
    total.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

function search() {
  q.page = 1
  load()
}

watch(tab, () => {
  q.page = 1
  load()
})

async function openDetail(row: RepayParentRow) {
  const on = row.orderNum?.trim()
  if (!on) {
    return
  }
  detailTitle.value = `还款详情 · ${on}`
  detailOrderNum.value = on
  detailRows.value = []
  detailDlg.value = true
  detailLoading.value = true
  try {
    const { data } = await fetchRepayDetails(on, 1, 200)
    detailRows.value = data.content ?? []
  } finally {
    detailLoading.value = false
  }
}

function onPage() {
  load()
}

function onSize(s: number) {
  q.rows = s
  q.page = 1
  load()
}

onMounted(load)
</script>

<template>
  <PageChrome
    title="还款明细"
    description="对标旧版 Daikuan/voucher（Awaiting repayment）与已还父订单列表。待还款对应父订单 status=16（分期还款中）；已还订单对应 status=12。"
  >
    <el-card shadow="hover" class="ep-table-scope rp-card">
      <el-tabs v-model="tab" class="rp-tabs">
        <el-tab-pane label="待还款（Awaiting repayment）" name="awaiting" />
        <el-tab-pane label="已还订单（Repaid）" name="repaid" />
      </el-tabs>

      <el-form inline class="rp-toolbar ep-filter-panel-inner">
        <el-form-item label="手机号">
          <el-input
            v-model="q.keyword"
            clearable
            placeholder="模糊搜索 user"
            style="width: 200px"
            @keyup.enter="search"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">筛选</el-button>
          <el-button :loading="loading" @click="load">刷新</el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="rows" stripe border size="default" empty-text="暂无数据">
        <el-table-column prop="id" label="ID" width="76" align="center" />
        <el-table-column prop="orderNum" label="订单号" min-width="132" show-overflow-tooltip />
        <el-table-column prop="user" label="手机号" width="132" show-overflow-tooltip />
        <el-table-column label="客户姓名" width="112" show-overflow-tooltip>
          <template #default="{ row }">{{ (row as RepayParentRow).customerName?.trim() || '—' }}</template>
        </el-table-column>
        <el-table-column label="借款总额" width="108" align="right">
          <template #default="{ row }">{{ fmtMoney((row as RepayParentRow).money) }}</template>
        </el-table-column>
        <el-table-column label="总期数" width="84" align="center">
          <template #default="{ row }">{{ (row as RepayParentRow).months ?? '—' }}</template>
        </el-table-column>
        <el-table-column label="创建时间" width="168" align="center">
          <template #default="{ row }">{{ formatEpoch((row as RepayParentRow).addtime ?? undefined) }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="112" align="center">
          <template #default="{ row }">
            <el-button type="primary" link @click="openDetail(row as RepayParentRow)">还款详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="ep-pager-footer pager">
        <el-pagination
          v-model:current-page="q.page"
          v-model:page-size="q.rows"
          :total="total"
          :page-sizes="[10, 15, 25, 50]"
          layout="sizes,prev,pager,next,total"
          background
          @current-change="onPage"
          @size-change="onSize"
        />
      </div>
    </el-card>

    <el-dialog v-model="detailDlg" :title="detailTitle" width="960px" destroy-on-close class="detail-dlg">
      <div v-loading="detailLoading">
        <el-alert
          title="以下为 voucher 分期表；对齐旧版 vodetail iframe。"
          type="info"
          :closable="false"
          class="mb08"
        />
        <el-table :data="detailRows" stripe border max-height="420" size="small">
          <el-table-column prop="id" label="ID" width="72" />
          <el-table-column prop="ordernum" label="订单号" min-width="120" />
          <el-table-column prop="user" label="手机号" width="110" />
          <el-table-column label="当前期" width="72" align="center">
            <template #default="{ row }">{{ instNo(row as VoucherRow) ?? '—' }}</template>
          </el-table-column>
          <el-table-column label="本金快照" width="96" align="right">
            <template #default="{ row }">{{ fmtMoney((row as VoucherRow).money) }}</template>
          </el-table-column>
          <el-table-column label="月供" width="96" align="right">
            <template #default="{ row }">{{ fmtMoney((row as VoucherRow).monthmoney) }}</template>
          </el-table-column>
          <el-table-column label="应还日" width="118" align="center">
            <template #default="{ row }">{{ fmtDt((row as VoucherRow).huantime) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="88" align="center">
            <template #default="{ row }">
              <el-tag :type="Number((row as VoucherRow).status) === 1 ? 'success' : 'warning'" size="small">
                {{ voucherStatusLabel((row as VoucherRow).status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="支付时间" width="168" align="center">
            <template #default="{ row }">{{ fmtDt((row as VoucherRow).paytime) }}</template>
          </el-table-column>
        </el-table>
        <div v-if="!detailLoading && detailRows.length === 0 && detailOrderNum" class="empty-hint">
          该父单暂无 voucher 明细（或未生成分期）
        </div>
      </div>
    </el-dialog>
  </PageChrome>
</template>

<style scoped lang="scss">
.rp-card {
  border-radius: var(--ep-radius-lg) !important;
}
.rp-tabs {
  padding: 4px 4px 0;
}
.ep-filter-panel-inner {
  padding: 4px 0 8px !important;
  margin: 0 0 8px !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}
.rp-toolbar {
  margin-bottom: 8px;
}
.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.mb08 {
  margin-bottom: 8px;
}
.empty-hint {
  padding: 12px;
  font-size: 13px;
  color: #909399;
}
</style>
